import { Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
} from 'react';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import dayjs from 'dayjs';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import {
	SEX_LIST,
	YESNO,
} from '@/common/constants/common';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import { useGetDicListQuery } from '@/api/common';
import SingleCheckbox from '@/common/components/form-inputs/SingleCheckbox';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

const EditMembershipInformationForm = forwardRef(
	({ type, setType, details }: Props, ref) => {
		const { t } = useTranslation();
		const defaultValues: DefaultValues<any> = {
			mailRequest: null,
			newsletterConfidential: null,
		};

		useEffect(() => {
			if (details) methods.reset(details);
		}, [details]);

		const formSchema = Yup.object().shape({
			mailRequest: Yup.string().required(t('errorMessage.pleaseSelect')),
			newsletterConfidential: Yup.string().required(
				t('errorMessage.pleaseSelect')
			),
		});
		const methods = useForm<any>({
			defaultValues,
			resolver: yupResolver(formSchema),
			mode: 'onChange',
			shouldFocusError: true,
		});

		useImperativeHandle(ref, () => ({
			getFormData: () => methods.getValues(),
			validateForm: () => {
				methods.trigger();
				methods.formState.isValid;
				return methods.trigger().then(() => methods.formState.isValid);
			},
		}));

		return (
			<>
				<FormProvider {...methods}>
					<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
						{t('preAdmission.membershipInformation')}
					</Typography>
					<form onSubmit={methods.handleSubmit((e) => e.preventDefault())}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.mailRequest') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='mailRequest'
									row
									options={YESNO}
								/>
							</Grid>
							<Grid size={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.newsletterConfidential') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='newsletterConfidential'
									row
									options={YESNO}
								/>
							</Grid>
						</Grid>
					</form>
				</FormProvider>
			</>
		);
	}
);

EditMembershipInformationForm.displayName = 'EditMembershipInformationForm';

export default EditMembershipInformationForm;
